import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { brandStory, testimonials } from '@/mocks/faq';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4pgdca23r0tr1aqig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString()
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (String(serverMsg).includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(String(serverMsg));
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="relative pt-20 pb-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' }
              ]}
            />
            <div className="mt-6 text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900">
                {brandStory.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base mt-3 max-w-[600px] mx-auto">
                {brandStory.subtitle}
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="relative w-full h-[350px] md:h-[450px] rounded-lg overflow-hidden mb-14">
              <img
                src="https://readdy.ai/api/search-image?query=A%20creative%20team%20workspace%20concept%20illustration%2C%20warm%20golden%20and%20amber%20lighting%2C%20Chinese%20ink%20painting%20style%20with%20modern%20elements%2C%20artistic%20and%20atmospheric%2C%20clean%20composition%20with%20soft%20background&width=1200&height=600&seq=about-hero-01&orientation=landscape"
                alt="挣扎吧亚当君制作团队"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {brandStory.sections.map((section, index) => (
              <div key={index} className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                  {section.title}
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed">
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section id="testimonials" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">
                —— 玩家口碑 ——
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                玩家评价
              </h2>
              <p className="text-foreground-500 text-sm md:text-base mt-3 max-w-[550px] mx-auto">
                来自真实玩家的心声，这是我们最大的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-heading font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-foreground-800 text-sm font-medium">{t.name}</p>
                      <p className="text-foreground-400 text-xs">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[700px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <p className="text-primary-600 text-sm font-medium tracking-wider mb-2">
                —— 联系我们 ——
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                与我们取得联系
              </h2>
              <p className="text-foreground-500 text-sm md:text-base mt-3">
                如果你对挣扎吧亚当君有任何疑问、建议或合作意向，欢迎通过以下表单与我们联系
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-lg p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500个字符）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="anti-spam-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <div className="mt-4 p-3 rounded-md bg-accent-100/50 text-accent-800 text-sm text-center">
                  <i className="ri-check-line mr-1"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mt-4 p-3 rounded-md bg-primary-100/50 text-primary-700 text-sm text-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}